/**
 * The consent page another app sends you to: "The Statement wants to read your entries".
 *
 * You are signed in here, as yourself, which is what proves this account is yours; you
 * choose the groups; and the one-time code goes only to the address the operator
 * registered for that app — shown on the page, so you can check it is the one you
 * expected. See `services/connect.py` for the whole handshake.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Spinner } from "../../components/ui/spinner";
import { ApiError, api, type Schemas } from "../../lib/api";
import { AuthShell } from "../auth/AuthPages";

type ConnectInfo = Schemas["ConnectInfoResponse"];
type Approved = Schemas["ApproveConnectResponse"];

/** Mirrors the server's check, so a broken link says so before anything is asked. */
const STATE = /^[A-Za-z0-9_\-.~]{16,128}$/;

export function ConnectPage() {
  const { clientId = "" } = useParams();
  const [params] = useSearchParams();
  const state = params.get("state") ?? "";
  const [chosen, setChosen] = useState<Set<number>>(new Set());

  const info = useQuery({
    queryKey: ["connect", clientId],
    queryFn: () => api.get<ConnectInfo>(`/connect/${encodeURIComponent(clientId)}`),
    retry: false,
  });

  // Start from what is already shared, or everything when this is the first time.
  useEffect(() => {
    if (!info.data) return;
    const current = info.data.current_group_ids;
    setChosen(new Set(current.length ? current : info.data.groups.map((g) => g.id)));
  }, [info.data]);

  const approve = useMutation({
    mutationFn: () =>
      api.post<Approved>(`/connect/${encodeURIComponent(clientId)}`, {
        group_ids: [...chosen],
        state,
      }),
    // A full navigation to the other app, carrying the one-time code.
    onSuccess: (approved) => window.location.assign(approved.redirect_url),
  });

  if (!STATE.test(state)) {
    return (
      <AuthShell title="This link is incomplete">
        <p className="text-sm text-muted">
          Start again from the other app's settings page — the link that brought you here is
          missing a part it needs.
        </p>
      </AuthShell>
    );
  }
  if (info.isPending) {
    return (
      <AuthShell title="Connecting an app">
        <Spinner />
      </AuthShell>
    );
  }
  if (info.isError || !info.data) {
    return (
      <AuthShell title="This app cannot be connected">
        <p className="text-sm text-muted">
          {info.error instanceof ApiError ? info.error.message : "Something went wrong."}
        </p>
      </AuthShell>
    );
  }

  const app = info.data;
  const toggle = (id: number) =>
    setChosen((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <AuthShell title={`Connect ${app.name}`}>
      <p className="mb-4 text-sm">
        <strong>{app.name}</strong> wants to read your entries in the groups you choose: what
        was spent, who paid, and your share. It cannot add, change or delete anything here,
        and you can disconnect it any time in Settings.
      </p>

      {app.groups.length === 0 ? (
        <p className="mb-5 text-sm text-muted">You are not in any groups yet.</p>
      ) : (
        <fieldset className="mb-5 flex flex-col gap-1">
          <legend className="mb-2 text-2xs uppercase tracking-widest text-muted">
            Share these groups
          </legend>
          {app.groups.map((group) => (
            <label
              key={group.id}
              className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 hover:bg-ink/[0.03]"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-[rgb(var(--accent))]"
                checked={chosen.has(group.id)}
                onChange={() => toggle(group.id)}
              />
              <span className="text-sm">
                {group.name}
                <span className="text-muted"> · as {group.member_name}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}

      <p className="mb-5 text-xs text-muted">
        A one-time code goes to <span className="num">{app.callback_origin}</span>. If that
        is not the address of your {app.name}, stop here.
      </p>

      {approve.isError ? (
        <p role="alert" className="mb-3 text-sm text-owing">
          {approve.error instanceof ApiError ? approve.error.message : "That did not work."}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={chosen.size === 0 || approve.isPending}
          onClick={() => approve.mutate()}
        >
          {approve.isPending ? "Connecting…" : "Allow"}
        </Button>
        <Button asChild variant="ghost">
          <Link to="/">Cancel</Link>
        </Button>
      </div>
    </AuthShell>
  );
}
